import { useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { usePageBuilderTopBar } from "./hooks/usePageBuilderTopBar";
import { usePageBuilderPublishMutation } from "./hooks/usePageBuilderPublishMutation";
import { PageBuilderTopBarModals } from "../components/PageBuilderTopbarModals";
import { usePublishGate } from "./hooks/usePublishGate";
import { colors } from "../../../../ui/colors";
import { usePageBuilderMyQuotaQuery } from "../../../workspace/hooks/usePageBuilderQuotaQuery";
import { SaveDraftButton } from "../components/SaveDraftButton";
import { useProject } from "../../project/context/ProjectContext";

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function PageBuilderTopBar() {
  const quotaQuery = usePageBuilderMyQuotaQuery();

  const {
    isReady,
    editorRef,
    templates,
    activeTemplateId,
    setActiveTemplateId,
    slugName,
    handleChangeSlug,
    html,
    publish,
  } = usePageBuilderTopBar();

  const { projectName, setProjectName, projectIconDataUrl, setProjectIconDataUrl } = useProject();

  const { draftId } = useParams<{ draftId: string }>();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type"); // "draft" | "publish" | null

  const isDraft = type === "draft";
  const isSite = type === "publish" || !type;

  const { mutateAsync } = usePageBuilderPublishMutation({
    slugName,
    draftId: draftId || "",
  });


  const canCreate = quotaQuery.data?.data?.canCreate;

  const { onClickPublish, onConfirmPublish } = usePublishGate({
    isReady,
    quotaRefetch: quotaQuery.refetch,
    quotaLoading: quotaQuery.isLoading,
    canCreate,
    openPublishModal: publish.openPublishModal,
    openPublishedSiteModal: publish.openPublishedSiteModal,
    closePublishModal: publish.closePublishModal,
    publishSite: () => mutateAsync(),
  });

  // Editable Project Name UI
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState(projectName);
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useMemo(() => {
    setDraftName(projectName);
  }, [projectName]);

  const onStartEditName = () => {
    setDraftName(projectName);
    setIsEditingName(true);
  };

  const onCommitName = () => {
    const v = draftName.trim() || "New Project";
    setProjectName(v);
    setIsEditingName(false);
  };

  const onCancelName = () => {
    setDraftName(projectName);
    setIsEditingName(false);
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onPickIcon = () => fileInputRef.current?.click();

  const onIconSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;

    const dataUrl = await fileToDataUrl(file);
    setDataUrl(dataUrl);
    setProjectIconDataUrl(file);

    e.target.value = "";
  };

  return (
    <>
      <div style={topbarStyle}>
        <div style={leftSectionStyle}>
          {/* Back button (a la izquierda) */}
          <button
            style={returnToFlownectButtonStyle}
            onClick={() => (window.location.href = "/business/page-builder")}
          >
            Return to Flownect
          </button>

          {/* Icono editable (espacio en blanco) */}
          <div style={iconBoxStyle} onClick={onPickIcon} title="Edit project icon">
            {projectIconDataUrl ? (
              <img src={dataUrl} alt="Project icon" style={iconImgStyle} />
            ) : (
              <span style={iconPlaceholderStyle}>+</span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={onIconSelected}
          />

          {/* Nombre + slug */}
          <div style={projectNameContainerStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              {!isEditingName ? (
                <div style={projectTitleStyle} onClick={onStartEditName} title="Click to rename">
                  {projectName}
                </div>
              ) : (
                <input
                  autoFocus
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onBlur={onCommitName}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onCommitName();
                    if (e.key === "Escape") onCancelName();
                  }}
                  style={projectTitleInputStyle}
                />
              )}
            </div>

            <div style={projectNameStyle}>{`marketing/${slugName}`}</div>
          </div>
        </div>

        <div style={rightSectionStyle}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: "6px 10px",
              borderRadius: 999,
              background: isDraft ? "var(--fx-colors-yellow-100, #FFF9C2)" : "var(--fx-colors-success-100, #D1FAE5)",
              color: isDraft ? "#92400E" : "#166534",
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
              userSelect: "none",
            }}
          >
            {isDraft ? "DRAFT" : "PUBLISHED"}
          </span>

          <button
            onClick={() => editorRef.current?.runCommand("fullscreen")}
            style={{ ...actionButton, ...previewButtonStyle }}
            disabled={!isReady}
          >
            Preview
          </button>

          {isDraft && (
            <SaveDraftButton
              favicon={projectIconDataUrl}
              draftId={draftId ?? ""}
              editor={editorRef.current}
              projectName={projectName}
            />
          )}

          <button
            onClick={onClickPublish}
            style={{ ...actionButton, ...publishButtonStyle }}
            disabled={!isReady || quotaQuery.isLoading}
          >
            {isSite ? "Publish Update" : "Publish Site"}
          </button>
        </div>
      </div>

      <PageBuilderTopBarModals
        html={html}
        publish={publish}
        slugName={slugName}
        handleChangeSlug={handleChangeSlug}
        projectName={projectName}
        activeTemplateId={activeTemplateId}
        quota={{ isLoading: quotaQuery.isLoading, canCreate }}
        publishSite={onConfirmPublish}
        onUpdateExistingSite={async () => {
          await mutateAsync();
        }}
        onContinueToPayment={() => {
          // window.location.href = checkoutUrl;
        }}
      />
    </>
  );
}

// Styles
const topbarStyle: React.CSSProperties = {
  height: 64,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0 24px",
  background: "var(--fx-surface-base, #FFFFFF)",
  borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  zIndex: 10,
};

const leftSectionStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  flex: 1,
};

const projectNameContainerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const projectTitleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: "var(--fx-surface-text, #0F172A)",
  cursor: "pointer",
  padding: "2px 8px",
  borderRadius: 6,
  background: "var(--fx-surface-sunken, #F8FAFC)",
  border: "1px solid var(--fx-surface-border, #E2E8F0)",
  userSelect: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
};

const editHintStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: "var(--fx-surface-textsecondary, #64748B)",
  padding: "2px 6px",
  borderRadius: 999,
  border: "1px solid var(--fx-surface-border, #E2E8F0)",
  background: "var(--fx-surface-base, #FFFFFF)",
};

const projectTitleInputStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid var(--fx-surface-borderstrong, #CBD5E1)",
  outline: "none",
  minWidth: 220,
};

const projectNameStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: colors.orange.base,
  cursor: "pointer",
  padding: "2px 8px",
  borderRadius: 4,
  transition: "all 0.2s",
  userSelect: "none",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--fx-surface-textsecondary, #64748B)",
  letterSpacing: "0.5px",
  paddingLeft: 8,
};

const rightSectionStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  flex: 1,
  justifyContent: "flex-end",
};

const actionButton: React.CSSProperties = {
  padding: "10px 20px",
  borderRadius: 8,
  border: "none",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 600,
  transition: "all 0.2s",
  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
};

const previewButtonStyle: React.CSSProperties = {
  background: "var(--fx-surface-base, #FFFFFF)",
  color: "var(--fx-surface-textsecondary, #64748B)",
  border: "1px solid var(--fx-surface-borderstrong, #CBD5E1)",
};

const publishButtonStyle: React.CSSProperties = {
  background: colors.orange.base,
  color: "#ffffff",
};

const returnToFlownectButtonStyle: React.CSSProperties = {
  background: "#fb8e00",
  color: "#ffffff",
  border: "1px solid #fb8e00",
  padding: "8px 16px",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 600,
  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
};

const iconBoxStyle: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 8,
  border: "1px dashed var(--fx-surface-borderstrong, #CBD5E1)",
  background: "var(--fx-surface-base, #FFFFFF)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  overflow: "hidden",
};

const iconImgStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const iconPlaceholderStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: "var(--fx-surface-texttertiary, #94A3B8)",
  userSelect: "none",
};
